import type {
  AsymmetricCoreCrypto,
  SenderPrivateKey,
  SenderPublicKey,
} from 'metaserver/static/js/encrypted_folder/crypto/asymmetric_core_crypto';
import {WebcryptoHPKESuite} from 'metaserver/static/js/encrypted_folder/crypto/hpke/webcryptoHpkeSuite';
import {
  AlgorithmNotFoundError,
  KeyMismatchError,
} from 'metaserver/static/js/encrypted_folder/crypto/errors';
import type {
  Algorithm,
  EncryptedFileKeyDraft,
  EncryptedKeyPairDraft,
  SymmetricKey,
  KeyPairDraft,
  PublicKey,
  PublicKeyDraft,
} from 'metaserver/static/js/encrypted_folder/crypto/types';
import {KeyEncoding} from 'metaserver/static/js/encrypted_folder/crypto/hpke/keyEncoding';
import {concat} from 'metaserver/static/js/encrypted_folder/crypto/hpke/utils';
import {bytesToHexString} from 'metaserver/static/js/encrypted_folder/utils/bytes';
import {openBase, sealBase} from 'metaserver/static/js/encrypted_folder/crypto/hpke/hpkeSuite';

export class P256AsymmetricCoreCrypto implements AsymmetricCoreCrypto {
  static algorithm: Algorithm = 'HPKE_DHKEM_P256_HKDF_SHA256_HKDF_SHA256_AES256_GCM';

  senderAuthenticated(): boolean {
    return false;
  }

  async generateKeyPair(): Promise<KeyPairDraft> {
    const {publicKey, privateKey} = await WebcryptoHPKESuite.generateKeyPair();
    const privateKeyData = await KeyEncoding.toPKCS8(privateKey);
    const publicKeyData = await KeyEncoding.toDer(publicKey);

    return {
      algorithm: P256AsymmetricCoreCrypto.algorithm,
      privateKeyData: privateKeyData,
      publicKey: {
        keyData: publicKeyData,
        algorithm: P256AsymmetricCoreCrypto.algorithm,
      },
    };
  }

  async encryptKeyPair(
    plainText: KeyPairDraft,
    key: PublicKeyDraft,
    _: SenderPrivateKey
  ): Promise<EncryptedKeyPairDraft> {
    if (key.algorithm !== P256AsymmetricCoreCrypto.algorithm) {
      throw new AlgorithmNotFoundError();
    }

    const deserializedPublicKey = await KeyEncoding.fromDer(key.keyData);
    const {encappedKey, cipherText} = await sealBase(
      WebcryptoHPKESuite,
      deserializedPublicKey,
      new Uint8Array(0),
      plainText.privateKeyData
    );
    const encryptedPrivateKey = concat([encappedKey, cipherText]);

    return {
      algorithm: plainText.algorithm,
      encryptedPrivateKeyData: encryptedPrivateKey,
      publicKeyData: plainText.publicKey.keyData,
    };
  }

  async decryptKeyPair(
    cipherText: EncryptedKeyPairDraft,
    key: KeyPairDraft,
    _: SenderPublicKey
  ): Promise<KeyPairDraft> {
    if (key.publicKey.algorithm !== P256AsymmetricCoreCrypto.algorithm) {
      throw new AlgorithmNotFoundError();
    }

    const encodedPrivateKeyData = cipherText.encryptedPrivateKeyData;
    const enc = encodedPrivateKeyData.slice(0, WebcryptoHPKESuite.info().nEnc);
    const cipherTextData = encodedPrivateKeyData.slice(WebcryptoHPKESuite.info().nEnc);
    const deserializedPrivateKey = await KeyEncoding.fromPKCS8(key.privateKeyData);
    const decryptedData = await openBase(
      WebcryptoHPKESuite,
      enc,
      deserializedPrivateKey,
      new Uint8Array(0),
      cipherTextData
    );

    return {
      algorithm: cipherText.algorithm,
      privateKeyData: decryptedData,
      publicKey: {
        keyData: cipherText.publicKeyData,
        algorithm: cipherText.algorithm,
      },
    };
  }

  async encryptKey(
    plainText: SymmetricKey,
    key: PublicKey,
    _: SenderPrivateKey
  ): Promise<EncryptedFileKeyDraft> {
    if (key.algorithm !== P256AsymmetricCoreCrypto.algorithm) {
      throw new AlgorithmNotFoundError();
    }

    const deserializedPublicKey = await KeyEncoding.fromDer(key.keyData);
    const {encappedKey, cipherText} = await sealBase(
      WebcryptoHPKESuite,
      deserializedPublicKey,
      new Uint8Array(0),
      plainText.keyData
    );
    const encryptedKey = concat([encappedKey, cipherText]);

    return {
      algorithm: plainText.algorithm,
      encryptedKeyData: encryptedKey,
      encryptingKeyId: key.keyId,
    };
  }

  async decryptKey(
    cipherText: EncryptedFileKeyDraft,
    key: KeyPairDraft,
    _: SenderPublicKey
  ): Promise<SymmetricKey> {
    if (key.publicKey.algorithm !== P256AsymmetricCoreCrypto.algorithm) {
      throw new AlgorithmNotFoundError();
    }

    const encodedKeyData = cipherText.encryptedKeyData;
    const enc = encodedKeyData.slice(0, WebcryptoHPKESuite.info().nEnc);
    const cipherTextData = encodedKeyData.slice(WebcryptoHPKESuite.info().nEnc);
    const deserializedPrivateKey = await KeyEncoding.fromPKCS8(key.privateKeyData);
    const decryptedData = await openBase(
      WebcryptoHPKESuite,
      enc,
      deserializedPrivateKey,
      new Uint8Array(0),
      cipherTextData
    );

    return {
      algorithm: cipherText.algorithm,
      keyData: decryptedData,
    };
  }

  async validateKeyPair(keyPair: KeyPairDraft): Promise<void> {
    const decodedPrivateKey = await KeyEncoding.fromPKCS8(keyPair.privateKeyData);
    const extractedPublicKey = await WebcryptoHPKESuite.pkFromSk(decodedPrivateKey);
    const encodedExtractedPublicKey = await KeyEncoding.toDer(extractedPublicKey);

    // Re-encode the provided public key to DER format to prevent minor encoding differences to cause a fail
    const providedPublicKey = await KeyEncoding.fromDer(keyPair.publicKey.keyData);
    const encodedProvidedPublicKey = await KeyEncoding.toDer(providedPublicKey);

    if (
      bytesToHexString(encodedExtractedPublicKey) !== bytesToHexString(encodedProvidedPublicKey) ||
      keyPair.algorithm !== P256AsymmetricCoreCrypto.algorithm ||
      keyPair.publicKey.algorithm !== P256AsymmetricCoreCrypto.algorithm
    ) {
      throw new KeyMismatchError();
    }
  }
}
